// src/services/apiService.ts
// (Replace your entire file with this)

export type AuthType = "none" | "app" | "mikrotik";

export interface FetchOptions extends RequestInit {
  auth?: AuthType;
  skipAuthRedirect?: boolean;
}

/**
 * ✅ Production-safe API base:
 * - relative path so it works on any domain (aamx.alsa.life, localhost, etc.)
 * - avoids any env injection problems
 */
const API_BASE_URL = "/api/v1";

export const API_CONFIG = {
  baseUrl: API_BASE_URL,
  endpoints: {
    clients: "/clients/",
    bandwidth: "/bandwidth",
    revenue: "/revenue",
    alerts: "/alerts",
    roles: "/roles/",
  },
};

const getAppToken = () => localStorage.getItem("token");
const getMikrotikToken = () => localStorage.getItem("mikrotikToken");

/**
 * Join base + endpoint safely:
 * - base "/api/v1" + "/auth/login" -> "/api/v1/auth/login"
 * - base "/api/v1/" + "auth/login" -> "/api/v1/auth/login"
 */

const joinUrl = (base: string, endpoint: string) => {
  const rawBase = (base || "").trim();
  const rawEndpoint = (endpoint || "").trim();

  // Absolute URL passthrough
  if (/^https?:\/\//i.test(rawEndpoint)) return rawEndpoint;

  // Normalize base (remove trailing slash)
  let b = rawBase.replace(/\/+$/, "");
  if (!b || b === "undefined" || b === "null") b = "/api/v1";

  // Normalize endpoint (ensure leading slash)
  let e = rawEndpoint.startsWith("/") ? rawEndpoint : `/${rawEndpoint}`;

  // ✅ De-dupe: if endpoint already starts with base, don't prepend base again
  // Example: base="/api/v1", endpoint="/api/v1/finance" -> keep endpoint only
  if (e.startsWith(`${b}/`) || e === b) {
    return e;
  }

  // ✅ Extra safety: prevent "/api/v1/api/v1/..." if someone passed it twice
  // (handles cases like base="/api/v1" and endpoint="/api/v1/api/v1/x")
  if (b === "/api/v1" && e.startsWith("/api/v1/api/v1/")) {
    e = e.replace(/^\/api\/v1\/api\/v1\//, "/api/v1/");
  }

  return `${b}${e}`;
};

/**
 * Build auth headers based on auth type.
 */
const buildAuthHeaders = (auth: AuthType): HeadersInit => {
  const headers: HeadersInit = { "Content-Type": "application/json" };

  let token: string | null = null;
  if (auth === "app") token = getAppToken();
  if (auth === "mikrotik") token = getMikrotikToken();

  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
};

/**
 * Core fetch helper used by all API calls.
 * - Normalises network errors ("Failed to fetch") into friendly text
 * - Handles 401 for app auth (logs out + redirects)
 * - Parses JSON once and exposes clean error messages
 */
export const fetchData = async <T = any>(
  endpoint: string,
  options: FetchOptions = {},
): Promise<T> => {
  const { auth = "app", skipAuthRedirect = false, headers, ...rest } = options;

  const finalHeaders: HeadersInit = {
    ...buildAuthHeaders(auth),
    ...(headers || {}),
  };

  const url = joinUrl(API_CONFIG.baseUrl, endpoint);

  let response: Response;

  // ─────────────────────────────────────────────
  // Network errors (CORS, offline, server down)
  // ─────────────────────────────────────────────
  try {
    response = await fetch(url, {
      method: rest.method || "GET",
      ...rest,
      headers: finalHeaders,
    });
  } catch (networkErr: any) {
    const friendly =
      networkErr?.message === "Failed to fetch"
        ? "Unable to reach server. Please check your internet connection or router status."
        : networkErr?.message || "Network error. Please try again.";

    const err: any = new Error(friendly);
    err.status = 0;
    throw err;
  }

  // ─────────────────────────────────────────────
  // 401 handling for app auth
  // ─────────────────────────────────────────────
  if (response.status === 401 && auth === "app" && !skipAuthRedirect) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";

    const err: any = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }

  // ─────────────────────────────────────────────
  // Read body once (works for success + error)
  // ─────────────────────────────────────────────
  const rawText = await response.text().catch(() => "");
  let data: any = null;

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      // Non-JSON response (plain text, HTML, etc.)
      data = rawText;
    }
  }

  // ─────────────────────────────────────────────
  // Error branch – build friendly message
  // ─────────────────────────────────────────────
  if (!response.ok) {
    let message =
      typeof data === "string"
        ? data
        : data?.detail ||
          data?.error ||
          data?.message ||
          `API Error: ${response.status}`;

    if (message === "Failed to fetch") {
      message = "Unable to reach server. Please try again later.";
    }
    if (typeof message === "string" && message.includes("ECONNREFUSED")) {
      message = "Server is offline or unreachable.";
    }

    const err: any = new Error(message);
    err.status = response.status;
    err.data = data;
    throw err;
  }

  // ─────────────────────────────────────────────
  // Success branch
  // ─────────────────────────────────────────────
  if (response.status === 204 || rawText === "") {
    return undefined as T;
  }

  return data as T;
};

/**
 * Convenience wrapper for app-authenticated API calls
 */
export const fetchApp = <T = any>(
  endpoint: string,
  options: Omit<FetchOptions, "auth"> = {},
) => fetchData<T>(endpoint, { ...options, auth: "app" });

/**
 * Convenience wrapper for Mikrotik-authenticated API calls
 */
export const fetchMikrotik = <T = any>(
  endpoint: string,
  options: Omit<FetchOptions, "auth"> = {},
) => fetchData<T>(endpoint, { ...options, auth: "mikrotik" });

/**
 * Optional: Login helper so login page doesn’t construct URLs manually.
 * Use: const data = await login(email, password);
 */
export const login = async (email: string, password: string) => {
  return fetchData<{ access_token?: string; token?: string }>("/auth/login", {
    auth: "none",
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
};
